// LoginPage.js - Premium login interface
import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Link,
  Snackbar,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import FormField from '../shared/FormField';
import AppButton from '../shared/AppButton';

const MAX_ATTEMPTS = 5;
const BASE_LOCKOUT_SECONDS = 30;
const RATE_LIMIT_KEY = 'loginRateLimit';

const getRateLimitState = () => {
  try {
    const raw = localStorage.getItem(RATE_LIMIT_KEY);
    if (!raw) return { attempts: 0, lockoutUntil: 0, consecutiveLockouts: 0 };
    return JSON.parse(raw);
  } catch {
    return { attempts: 0, lockoutUntil: 0, consecutiveLockouts: 0 };
  }
};

const setRateLimitState = (state) => {
  localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(state));
};

const clearRateLimitState = () => {
  localStorage.removeItem(RATE_LIMIT_KEY);
};

const LoginPage = () => {
  const { login, resetPassword, error: authError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [resetSent, setResetSent] = useState(false);

  // Rate-limiting state
  const [rateLimit, setRateLimit] = useState(() => getRateLimitState());
  const [countdown, setCountdown] = useState(0);

  const isLocked = Date.now() < rateLimit.lockoutUntil;
  const lockoutSecondsLeft = Math.max(0, Math.ceil((rateLimit.lockoutUntil - Date.now()) / 1000));

  // Countdown ticker
  React.useEffect(() => {
    if (!isLocked) {
      setCountdown(0);
      return;
    }
    setCountdown(lockoutSecondsLeft);
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((rateLimit.lockoutUntil - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        setRateLimit((prev) => {
          const next = { ...prev, lockoutUntil: 0 };
          setRateLimitState(next);
          return next;
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [isLocked, lockoutSecondsLeft, rateLimit.lockoutUntil]);

  const validateEmail = (value) => {
    if (!value) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
    return '';
  };

  const handleEmailBlur = () => {
    setFieldErrors((prev) => ({ ...prev, email: validateEmail(email) }));
  };

  const handlePasswordBlur = () => {
    setFieldErrors((prev) => ({
      ...prev,
      password: password ? '' : 'Password is required',
    }));
  };

  const handleForgotPassword = async () => {
    const emailError = validateEmail(email);
    setFieldErrors((prev) => ({ ...prev, email: emailError }));
    if (emailError) return;

    setLoading(true);
    const result = await resetPassword(email);
    setLoading(false);
    if (result.success) {
      setResetSent(true);
    } else {
      setError(result.error || 'Failed to send reset email. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const emailError = validateEmail(email);
    const passwordError = password ? '' : 'Password is required';
    setFieldErrors({ email: emailError, password: passwordError });

    if (emailError || passwordError) return;

    if (isLocked) {
      setError(`Too many failed attempts. Please wait ${countdown} second${countdown !== 1 ? 's' : ''}.`);
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);

      if (result.success) {
        clearRateLimitState();
        navigate('/', { replace: true });
      } else {
        // Track failed attempt
        const current = getRateLimitState();
        const newAttempts = current.attempts + 1;
        let nextState;

        if (newAttempts >= MAX_ATTEMPTS) {
          const multiplier = Math.pow(2, current.consecutiveLockouts);
          const lockoutDuration = BASE_LOCKOUT_SECONDS * multiplier * 1000;
          nextState = {
            attempts: 0,
            lockoutUntil: Date.now() + lockoutDuration,
            consecutiveLockouts: current.consecutiveLockouts + 1,
          };
        } else {
          nextState = { ...current, attempts: newAttempts };
        }

        setRateLimitState(nextState);
        setRateLimit(nextState);
        setError(result.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // If already logged in, redirect to dashboard (prevents back-button to login)
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 420,
          p: 4,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Logo/Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Attendance & Salary
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to your account
          </Typography>
        </Box>

        {/* Error / Warning Alert */}
        {(error || authError || isLocked) && (
          <Alert severity={isLocked ? 'warning' : 'error'} sx={{ mb: 3 }} onClose={() => setError('')}>
            {isLocked
              ? `Too many failed attempts. Please wait ${countdown} second${countdown !== 1 ? 's' : ''} before trying again.`
              : error || authError}
          </Alert>
        )}

        {/* Attempts remaining warning */}
        {!isLocked && rateLimit.attempts > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {rateLimit.attempts} failed attempt{rateLimit.attempts !== 1 ? 's' : ''}.{' '}
            {MAX_ATTEMPTS - rateLimit.attempts} attempt{MAX_ATTEMPTS - rateLimit.attempts !== 1 ? 's' : ''} remaining before lockout.
          </Alert>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          <FormField label="Email Address" required error={fieldErrors.email} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleEmailBlur}
              disabled={loading || isLocked}
              error={!!fieldErrors.email}
            />
          </FormField>

          <FormField label="Password" required error={fieldErrors.password} sx={{ mb: 1 }}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={handlePasswordBlur}
              disabled={loading || isLocked}
              error={!!fieldErrors.password}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading || isLocked}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </FormField>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Link
              component={RouterLink}
              to="#"
              onClick={(e) => {
                e.preventDefault();
                handleForgotPassword();
              }}
              variant="body2"
              underline="hover"
            >
              Forgot password?
            </Link>
          </Box>

          <AppButton
            type="submit"
            variant="primary"
            fullWidth
            size="large"
            disabled={loading || isLocked}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : isLocked ? (
              `Locked (${countdown}s)`
            ) : (
              'Sign in'
            )}
          </AppButton>
        </form>

        {/* Footer */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Contact your administrator for account access.
          </Typography>
        </Box>
      </Paper>

      <Snackbar
        open={resetSent}
        autoHideDuration={6000}
        onClose={() => setResetSent(false)}
        message="Password reset email sent. Check your inbox."
      />
    </Box>
  );
}

export default LoginPage;
